import { Controller } from "../../lib/stimulus-shim"

// Tracking des liens sortants (deep links inclus) + navigation app/web.
//
// Événements émis (platform_interaction, un seul « click » par clic physique —
// l'agrégateur ne compte que action=click dans total_clicks) :
// - click        : le clic sortant (click_type deep/web)
// - app_opened   : l'app native s'est ouverte (détection par visibilité)
// - web_fallback : app absente, bascule navigateur
//
// Navigation mobile : URL https + App Links/Universal Links (l'OS route vers
// l'app installée, le web sinon). Le chemin custom-scheme (iframe) ne sert
// qu'aux plateformes sans universal link — c'est le seul où l'issue app/web
// est observable (pagehide/visibilitychange : page cachée = app ouverte, page
// toujours là après 1,6 s = web_fallback). Sur le chemin universal, la page
// navigue dans les deux cas : seule la tentative (click_type=deep) est comptée.
export default class extends Controller {
  static values = {
    webUrl: String,
    deepUrl: String,
    intentUrl: String,
    universalUrl: String,
    platform: String,
    obfuscated: Boolean,
    moduleId: String,
    moduleType: String
  }

  connect() {
    if (this.obfuscatedValue) {
      this.boundKeydown = this.handleKeydown.bind(this)
      this.element.addEventListener("keydown", this.boundKeydown)
    }
  }

  disconnect() {
    if (this.boundKeydown) {
      this.element.removeEventListener("keydown", this.boundKeydown)
    }
    this.cancelAppWatch()
  }

  // Handle clicks on obfuscated links (spans)
  handleClick(event) {
    event.preventDefault()
    event.stopPropagation()

    const hasDeepLink = this.universalUrlValue || this.deepUrlValue
    const useDeep = this.isMobile() && hasDeepLink
    this.trackInteraction("click", useDeep ? "deep" : "web")

    if (!this.webUrlValue) return

    if (useDeep) {
      this.attemptDeepLink()
    } else {
      window.open(this.webUrlValue, "_blank")
    }
  }

  // Track clicks on regular links
  trackClick(event) {
    const hasDeepLink = this.universalUrlValue || this.deepUrlValue
    const useDeep = this.isMobile() && hasDeepLink
    this.trackInteraction("click", useDeep ? "deep" : "web")

    if (useDeep) {
      event.preventDefault()
      this.attemptDeepLink()
    }
  }

  // Handle keyboard navigation for obfuscated links
  handleKeydown(event) {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault()
      this.handleClick(event)
    }
  }

  // App Links (Android) / Universal Links (iOS) : naviguer vers l'URL https
  // et laisser l'OS router — app si installée, web sinon. JAMAIS d'intent://
  // ici : sans fallback déclaré, Chrome envoie au Play Store quand l'app
  // manque OU quand le scheme du template est périmé (constaté YouTube/GitHub).
  // Contrepartie assumée : la page navigue dans les deux cas, l'issue app/web
  // est inobservable sur ce chemin — seul le clic (click_type=deep) est compté.
  attemptDeepLink() {
    if (this.universalUrlValue) {
      window.location.href = this.universalUrlValue
      return
    }

    this.attemptDeepLinkLegacy()
  }

  // Custom scheme via iframe : la page reste vivante si l'app est absente,
  // la détection par visibilité est fiable ici
  attemptDeepLinkLegacy() {
    if (!this.deepUrlValue) {
      window.open(this.webUrlValue, "_blank")
      return
    }

    const iframe = document.createElement("iframe")
    iframe.style.display = "none"
    iframe.src = this.deepUrlValue
    document.body.appendChild(iframe)
    this.watchAppOpen({
      openWebOnTimeout: true,
      cleanupExtra: () => iframe.remove()
    })
  }

  // Écoute pagehide/visibilitychange pendant ~1.6s après la tentative :
  // page cachée = app ouverte (annule le fallback) ; sinon web_fallback
  watchAppOpen({ openWebOnTimeout = false, cleanupExtra = null } = {}) {
    this.cancelAppWatch()

    const onHidden = () => {
      this.cancelAppWatch()
      this.trackInteraction("app_opened", "app")
    }
    const onVisibility = () => { if (document.hidden) onHidden() }

    this.appWatch = {
      onHidden,
      onVisibility,
      cleanupExtra,
      timer: setTimeout(() => {
        this.cancelAppWatch()
        this.trackInteraction("web_fallback", "web")
        if (openWebOnTimeout && this.webUrlValue) {
          window.open(this.webUrlValue, "_blank")
        }
      }, 1600)
    }

    window.addEventListener("pagehide", onHidden)
    document.addEventListener("visibilitychange", onVisibility)
  }

  cancelAppWatch() {
    if (!this.appWatch) return

    clearTimeout(this.appWatch.timer)
    window.removeEventListener("pagehide", this.appWatch.onHidden)
    document.removeEventListener("visibilitychange", this.appWatch.onVisibility)
    this.appWatch.cleanupExtra?.()
    this.appWatch = null
  }

  // Track interaction with Ahoy
  trackInteraction(action, clickType) {
    if (typeof ahoy === "undefined") return
    // Propriétaire : ne pas compter ses propres clics
    if (document.querySelector('meta[name="rho-owner"]')) return

    const eventData = {
      platform: this.platformValue || "unknown",
      action: action,
      interaction_type: this.obfuscatedValue ? "obfuscated_link" : "direct_link",
      click_type: clickType,
      obfuscated: this.obfuscatedValue,
      url: this.webUrlValue,
      has_deep_link: !!this.deepUrlValue,
      has_universal_link: !!this.universalUrlValue
    }

    if (this.moduleIdValue) {
      eventData.module_id = this.moduleIdValue
      eventData.module_type = this.moduleTypeValue
    }

    const profileId = this.getProfileId()
    if (profileId) {
      eventData.profile_id = profileId
    }

    // page d'origine du clic : permettra la ventilation « liens par page »
    const pageEl = document.querySelector('[data-page-id]')
    if (pageEl?.dataset.pageId) {
      eventData.page_id = pageEl.dataset.pageId
    }

    ahoy.track("platform_interaction", eventData)
  }

  // Check if user is on mobile device
  isMobile() {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
  }

  // Profil courant : data-profile-id posé sur le <main> du layout profiles
  getProfileId() {
    const metaTag = document.querySelector('meta[name="profile-id"]')
    if (metaTag) return metaTag.content

    const profileElement = document.querySelector('[data-profile-id]')
    if (profileElement) return profileElement.dataset.profileId

    return null
  }
}
